import React from 'react';
import {
    Button,
    ButtonHierarchies,
    IconButton,
    Text,
    TextAs,
    TextSizes,
    Tooltip,
} from '@clockout-dev/design-elements';
import { DownOutlined, SyncOutlined } from '@ant-design/icons';
import SectionCard from '../../../common/sectionCard/SectionCard';
import UserCardRow from './UserCardRow';
import UserStatePill from '../../../common/userStatePill/UserStatePill';
import SyncDimensionDetail from './syncHistory/SyncDimensionDetail';
import { PAYROLL_SYNC_CARD_COPY, SYNC_HISTORY_COPY } from './syncHistory/syncHistory.constants';
import styles from './styles/payroll-sync-status-card.module.scss';
import historyStyles from './syncHistory/styles/sync-history.module.scss';

/**
 * "Payroll Sync Status" card (Figma 5747:1772) on the user page: the rollup
 * pill, one row per dimension, and, when the admin may see it, each row
 * expanding to the data the active sync stored (KAN-679).
 *
 * Renders `view` as-is. The view model is built by
 * `UserController.buildPayrollSyncStatusView` (rules in
 * `syncStoredData.utils.js`), and which rows are expanded is page state, so
 * this component holds no state and makes no decisions.
 *
 * @param {Object} props
 * @param {ReturnType<import('../syncStoredData.utils').buildPayrollSyncStatusView>} props.view
 * @param {() => void} props.onRetry Re-reads the sync status.
 * @param {(key: string) => void} props.onToggleDimension
 * @param {(() => void) | null} [props.onRetryStoredData] Re-reads the stored data; null hides Retry.
 * @param {() => void} props.onMarkComplete
 * @param {() => void} props.onDeleteLast
 * @param {boolean} [props.markCompleteLoading]
 * @param {boolean} [props.deleteLastLoading]
 * @param {boolean} [props.canDeleteSync] Gates "Delete Last Sync".
 * @param {boolean} [props.canMarkSyncComplete] Gates "Mark Sync Complete".
 *   Two flags, not one: the delete is unrecoverable and the mark-complete is
 *   not, so they are separate scopes server-side and a role may hold either.
 */
function PayrollSyncStatusCard({
    view,
    onRetry,
    onToggleDimension,
    onRetryStoredData = null,
    onMarkComplete,
    onDeleteLast,
    markCompleteLoading,
    deleteLastLoading,
    canDeleteSync = true,
    canMarkSyncComplete = true,
}) {
    return (
        <SectionCard icon={<SyncOutlined />} title={PAYROLL_SYNC_CARD_COPY.TITLE}>
            <div className={styles.rows}>
                <UserCardRow
                    label={PAYROLL_SYNC_CARD_COPY.OVERALL}
                    value={(
                        <Tooltip title={view.rollup.tooltip} placement="left">
                            <span role="status" aria-label={view.rollup.ariaLabel}>
                                <UserStatePill tone={view.rollup.tone} label={view.rollup.label} />
                            </span>
                        </Tooltip>
                    )}
                />
                {view.inconsistency ? (
                    <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.note}>
                        {view.inconsistency}
                    </Text>
                ) : null}

                {view.loadingText ? (
                    <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.note} role="status" aria-live="polite">
                        {view.loadingText}
                    </Text>
                ) : null}

                {view.errorText ? (
                    <div className={styles.errorRow} role="alert">
                        <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.errorText}>
                            {view.errorText}
                        </Text>
                        <Button hierarchy={ButtonHierarchies.SECONDARY} onClick={onRetry}>
                            {PAYROLL_SYNC_CARD_COPY.RETRY}
                        </Button>
                    </div>
                ) : null}

                {view.rows.map((row) => (
                    <React.Fragment key={row.key}>
                        <UserCardRow
                            label={row.label}
                            value={(
                                <span className={historyStyles.dimensionActions}>
                                    <Tooltip title={row.state.description} placement="left">
                                        {row.state.applicable ? (
                                            <span role="status" aria-label={row.stateLabel}>
                                                <UserStatePill tone={row.state.tone} label={row.state.label} />
                                            </span>
                                        ) : (
                                            <Text
                                                as={TextAs.SPAN}
                                                size={TextSizes.SMALL}
                                                className={styles.dimValueNa}
                                                aria-label={row.stateLabel}
                                            >
                                                {row.state.label}
                                            </Text>
                                        )}
                                    </Tooltip>
                                    {row.expandable ? (
                                        <IconButton
                                            icon={<DownOutlined className={row.expanded ? historyStyles.chevron : historyStyles.chevronCollapsed} />}
                                            aria-label={row.toggleLabel}
                                            aria-expanded={row.expanded}
                                            onClick={() => onToggleDimension(row.key)}
                                        />
                                    ) : null}
                                </span>
                            )}
                        />
                        {row.detail ? <SyncDimensionDetail view={row.detail} /> : null}
                    </React.Fragment>
                ))}

                {view.storedLoadingText ? (
                    <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.note} role="status">
                        {view.storedLoadingText}
                    </Text>
                ) : null}
                {view.storedErrorText ? (
                    <div className={historyStyles.storedState} role="alert">
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.errorText}>
                            {view.storedErrorText}
                        </Text>
                        {onRetryStoredData ? (
                            <Button hierarchy={ButtonHierarchies.SECONDARY} onClick={onRetryStoredData}>
                                {SYNC_HISTORY_COPY.RETRY}
                            </Button>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {canMarkSyncComplete || canDeleteSync ? (
                <div className={styles.footer}>
                    {canMarkSyncComplete ? (
                        <Button onClick={onMarkComplete} disabled={!!markCompleteLoading} className={styles.footerBtn}>
                            {PAYROLL_SYNC_CARD_COPY.MARK_COMPLETE}
                        </Button>
                    ) : null}
                    {canDeleteSync ? (
                        <Button
                            hierarchy={ButtonHierarchies.SECONDARY}
                            destructive
                            onClick={onDeleteLast}
                            disabled={!!deleteLastLoading}
                            className={styles.footerBtn}
                        >
                            {PAYROLL_SYNC_CARD_COPY.DELETE_LAST}
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </SectionCard>
    );
}

export default PayrollSyncStatusCard;
